import {useMemo} from "react";
import {StatisticsBundleWrapper} from "../model/statistics/statistics-group";
import {statisticsSelector, ttaStatisticsSelector} from "../store/statistics/selectors";
import {useSearchSettlementStatisticsQuery} from "../store/statistics/statistics-store";
import {SettlementEntity} from "../model/settlement";
import {
  CompanySettlementEntity,
  EMPTY_STATISTICS_BUNDLE_BUILDER_OPTIONS,
  settlementSelector,
  transportMethod,
  useGetSettlementQuery,
  useGetTtasBySettlementQuery
} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";

export type UseCompanySettlementStatisticsProps = {
  companySettlement: CompanySettlementEntity;
}

export type UseCompanySettlementStatisticsResult = {
  statistics?: StatisticsBundleWrapper
  settlement?: SettlementEntity
  isLoading?: boolean,
}

export const useCompanySettlementStatistics = (props: UseCompanySettlementStatisticsProps): UseCompanySettlementStatisticsResult => {
  const {
    companySettlement,
  } = props;

  const selectSettlement = useMemo(() => {
    return settlementSelector()
  }, []);

  const {settlement} = useGetSettlementQuery(companySettlement.settlementId, {
    selectFromResult: (result) => ({
      settlement: selectSettlement(result)
    })
  });

  const selectPipelineStatistics = useMemo(() => {
    return statisticsSelector()
  }, []);

  const pipelineStatisticsArg = useMemo(() => {
    if (companySettlement.method === transportMethod.pipeline) {
      return {
        settlementId: companySettlement.settlementId,
        includeSubTruckCompanies: true,
        includeTruckingData: false,
        includePipelineData: true,
        pipelineCompanyIds: [companySettlement.companyId],
      }
    } else {
      return skipToken
    }
  }, [companySettlement])

  const {pipelineStatistics, isPipelineStatisticsLoading} = useSearchSettlementStatisticsQuery(pipelineStatisticsArg, {
    selectFromResult: (result) => ({
      pipelineStatistics: selectPipelineStatistics(result, {
        ...EMPTY_STATISTICS_BUNDLE_BUILDER_OPTIONS,
        processVendorCompany: true,
      }),
      isPipelineStatisticsLoading: result.isLoading || result.isFetching,
    })
  });

  const selectTtaStatistics = useMemo(() => {
    return ttaStatisticsSelector()
  }, []);

  const truckingStatisticsArg = useMemo(() => {
    if (companySettlement.method === transportMethod.trucking) {
      return {
        settlementId: companySettlement.settlementId,
        criteria: {
          trailerCompanyId: companySettlement.companyId,
        },
      }
    } else {
      return skipToken
    }
  }, [companySettlement])

  const {truckingStatistics, isTruckingStatisticsLoading} = useGetTtasBySettlementQuery(truckingStatisticsArg, {
    selectFromResult: (result) => ({
      truckingStatistics: selectTtaStatistics(result, {
        ...EMPTY_STATISTICS_BUNDLE_BUILDER_OPTIONS,
        processTruckCompany: true,
      }),
      isTruckingStatisticsLoading: result.isLoading || result.isFetching,
    })
  })

  const statistics = useMemo(() => {
    return companySettlement.method === transportMethod.trucking ? truckingStatistics : pipelineStatistics
  }, [companySettlement, truckingStatistics, pipelineStatistics])

  return {
    statistics: statistics,
    isLoading: isPipelineStatisticsLoading || isTruckingStatisticsLoading,
    settlement: settlement,
  };
};
